package net.gorillagroove.sync.strategies

import kotlinx.serialization.Serializable
import kotlinx.serialization.json.JsonObject
import net.gorillagroove.api.Api
import net.gorillagroove.api.UserNotificationId
import net.gorillagroove.db.*
import net.gorillagroove.db.Database.userNotificationDao
import net.gorillagroove.notification.RawUserNotificationType
import net.gorillagroove.notification.UserNotificationService
import net.gorillagroove.sync.*
import net.gorillagroove.util.GGLog.logCrit
import net.gorillagroove.util.GGLog.logError
import net.gorillagroove.util.GGLog.logInfo
import net.gorillagroove.util.Instant
import net.gorillagroove.util.toMap

object UserNotificationSyncStrategy : BidirectionalStrategy {
    override val syncType: SyncableEntity = SyncableEntity.USER_NOTIFICATION

    override suspend fun syncDown(syncStatus: DbSyncStatus, onPageSyncedHandler: PageSyncHandler) {
        fetchSyncEntities<UserNotificationResponse>(syncType, syncStatus, onPageSyncedHandler) { changeSet ->
            Database.db.transaction {
                val modifiedNotifications = changeSet.newAndModified.map { it.asUserNotification() }
                UserNotificationService.save(modifiedNotifications, notify = false)

                if (changeSet.removed.isNotEmpty()) {
                    val deletedIds = changeSet.removed.map { UserNotificationId(it) }
                    userNotificationDao.deleteByIds(deletedIds)
                }
            }

            UserNotificationService.broadcast()
        }
    }

    override suspend fun syncUp() {
        val unsyncedNotifications = userNotificationDao.getAllUnsynchronized().many()
        if (unsyncedNotifications.isNotEmpty()) {
            logInfo("Found ${unsyncedNotifications.size} unsynced notifications")
        }

        val deleteIds = mutableSetOf<UserNotificationId>()
        val markReadIds = mutableSetOf<UserNotificationId>()

        unsyncedNotifications.forEach { dbNotification ->
            if (dbNotification.deleted) {
                deleteIds.add(dbNotification.id)
            } else if (dbNotification.read) {
                markReadIds.add(dbNotification.id)
            } else {
                logCrit("Unsynchronized notification found that had no reason to be unsynchronized! ID: ${dbNotification.id}")
                userNotificationDao.upsert(dbNotification.copy(synchronized = true))
            }
        }

        if (deleteIds.isEmpty() && markReadIds.isEmpty()) {
            return
        }

        val request = UserNotificationChangeRequest(
            deleteIds = deleteIds,
            markReadIds = markReadIds,
        )

        try {
            Api.patch<Unit>("user-notification", request)
        } catch (e: Exception) {
            logError("Failed to patch user notifications!", e)
            throw e
        }

        if (markReadIds.isNotEmpty()) {
            userNotificationDao.setSynchronized(id = markReadIds)
        }
        if (deleteIds.isNotEmpty()) {
            userNotificationDao.deleteByIds(deleteIds)
        }
    }
}

@Serializable
internal data class UserNotificationChangeRequest(
    val markReadIds: Set<UserNotificationId> = emptySet(),
    val deleteIds: Set<UserNotificationId> = emptySet(),
)

@Serializable
internal data class UserNotificationResponse(
    val id: UserNotificationId,
    val message: String,
    val read: Boolean,
    val createdAt: Instant,
    val updatedAt: Instant,
    val deleted: Boolean,
    val type: RawUserNotificationType,
    val data: JsonObject,
) {
    fun asUserNotification() = DbUserNotification(
        id = id,
        message = message,
        read = read,
        synchronized = true,
        createdAt = createdAt,
        updatedAt = updatedAt,
        deleted = deleted,
        type = type,
        data_ = data.toMap(),
    )
}
